import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';
import { trackEvent, trackPageLoad } from '@fff-web/fff-analytics';
import { UpdatePaymentModalV2, useCouponCode } from '@fff-web/fff-payments';
import { useUserContext } from '@fff-web/fff-utilities';
import AllErrorMessage from '../../components/AllErrorMessage';
import Layout from '../../components/Layout';
import useCouponContent from '../../hooks/useCouponContent';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import useShowErrorOrRedirectThankYouPage from '../../hooks/useShowPayErrorOrThankYouPage';
import {
  UpgradeMembershipProvider,
  useUpgradeMembershipContext,
} from '../../utils/ContextProviders/UpgradeContext';
import { useQueryParams } from '../../utils/hooks';
import { GROWTH_UPGRADE_MEMBERSHIP } from '../../utils/Optimizely/featureFlagVariations';
import { redirectToBaseSite } from '../../utils/redirectMethods';
import { couponScreenKey, Plans } from '../constants';
import env from '../MyAccount/utils/env';
import UpgradeControl from '../Upgrade/Upgrade';
import { UpgradeRedesign } from './UpgradeRedesign';

// This component hold the upgrade page logic
const UpgradeContainer = ({ ldClient }) => {
  const { userInfo } = useUserContext();
  const [disableClickWrapSending, setDisableClickWrapSending] = useState(false);
  const { growthUpgrade } = useUpgradeMembershipContext();
  const search = useQueryParams();
  const {
    coupon,
    price,
    handleReturnPrice,
    handleOnChange,
    // @ts-expect-error - TODO: Please review useCouponCode since handleSubmit is not returned by it.
    handleSubmit,
    resetInput,
  } = useCouponCode({ initialCouponCode: search.coupon_code });
  const { chatbotAppliedLab: showChatbot } = useFlags();

  const upgradeCaptchaRef = useRef(null);
  const history = useHistory();
  const thanksPage = '/upgrade-thanks';

  const { getCouponContentKey } = useCouponContent(couponScreenKey.UPGRADE);

  // TODO: Replace the submit logic to use only the coupon code string and not the object, now the upgrade page and reactivate page are using the same submit logic
  const discount = price?.now?.discount;
  const applyValidCoupon = parseFloat(discount) > 0;

  const couponsApplied = applyValidCoupon ? [{ code: coupon, type: 'coupon_code' }] : [];

  const { showRetry, showPayment, errorPaymentMessage, changeSubscription, onClose, isLoading } =
    useShowErrorOrRedirectThankYouPage(history, thanksPage, Plans.SEASONAL, couponsApplied);

  useLaunchDarkly(ldClient, userInfo);

  const isSeasonal =
    userInfo?.user?.subscriptionType === 'SEASONAL' &&
    userInfo?.user?.subscriptionState === 'ACTIVE';

  const hCaptcha = (
    <HCaptcha
      sitekey={env.hCaptchaSiteKey}
      onVerify={() => undefined}
      size="invisible"
      ref={upgradeCaptchaRef}
    />
  );

  useEffect(() => {
    if (isSeasonal) {
      trackPageLoad({
        pathName: '/upgrade-confirmation',
        properties: {
          title: 'Upgrade Page',
          coupon_code: search.coupon_code,
        },
      });
    }
  }, []);

  if (userInfo?.loggedIn && !isSeasonal) {
    redirectToBaseSite();
    return null;
  }

  // This component is used to render the upgrade page with the redesign or control variation
  const RenderUpgradeVariation =
    growthUpgrade.variationKey === GROWTH_UPGRADE_MEMBERSHIP.VARIATION_C
      ? UpgradeRedesign
      : UpgradeControl;

  const clickWrapConfiguration = {
    signerId: userInfo?.user.email,
    accessId: env.pactSafeAccessId,
    testMode: env.appEnv === 'dev',
    groupKey: env.pactSafeUpgradeGroupKey,
    groupId: env.pactSafeUpgradeGroupId,
    snapshotLocation: env.pactSafeUpgradeSnapshotId,
  };

  // TODO: Please fix this, hooks can't be called conditionally
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isCheckboxChecked = useCallback(() => {
    const checkboxElement = document.getElementById(
      `ps-contract-checkbox-group-${clickWrapConfiguration?.groupId}`,
    ) as HTMLInputElement;
    if (clickWrapConfiguration?.signerId && checkboxElement && !checkboxElement.checked) {
      checkboxElement.click();
      checkboxElement.disabled = true;
      setDisableClickWrapSending(true);
    }
    return checkboxElement;
  }, [clickWrapConfiguration?.groupId, clickWrapConfiguration?.signerId]);

  // TODO: Please fix this, hooks can't be called conditionally
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const memoizedIsCheckboxChecked = useMemo(() => isCheckboxChecked, [isCheckboxChecked]);

  const onSubmitSubscription = () => {
    upgradeCaptchaRef.current.resetCaptcha();
    trackEvent({
      ctaName: 'upgrade-confirmation',
      properties: {
        upgrading_plan: Plans.ANNUAL,
        CTA: 'upgrade-confirmation',
      },
    });
    upgradeCaptchaRef.current
      .execute({ async: true })
      .then(({ response: captchaToken }) => {
        memoizedIsCheckboxChecked();
        changeSubscription(Plans.ANNUAL, captchaToken);
      })
      .catch((err) => {
        console.error('Error processing you request:', err);
      });
  };

  return (
    <UpgradeMembershipProvider>
      <Layout
        isLoading={isLoading}
        showError={showRetry}
        redirectIfNoAuth
        ErrorComponent={() => <AllErrorMessage subjectAction="upgrade" onButtonClick={onClose} />}
        hideChatWidget={!showChatbot}
      >
        <RenderUpgradeVariation
          couponProps={{
            coupon,
            price,
            initialCouponCode: search.coupon_code,
            plan: Plans.ANNUAL,
            handleReturnPrice,
            handleOnChange,
            handleSubmit,
            resetInput,
          }}
          hCaptcha={hCaptcha}
          getCouponContentKey={getCouponContentKey}
          onSubmitSubscription={onSubmitSubscription}
          isLoading={isLoading}
        />
        <UpdatePaymentModalV2
          isOpen={showPayment}
          onClose={onClose}
          onSuccess={onSubmitSubscription}
          subtitle={errorPaymentMessage}
        />
        <div className="pactSafeWrapper">
          <PSClickWrap
            signerId={clickWrapConfiguration.signerId}
            accessId={clickWrapConfiguration.accessId}
            groupKey={clickWrapConfiguration.groupKey}
            snapshotLocation={clickWrapConfiguration.snapshotLocation}
            disableSending={disableClickWrapSending}
            displayImmediately={false}
            testMode={clickWrapConfiguration.testMode}
            clickWrapStyle="combined"
          />
        </div>
      </Layout>
    </UpgradeMembershipProvider>
  );
};

export { UpgradeContainer };
